import type { ComponentPropsWithoutRef } from "react";
import { Icon, type IconComponent } from "../Icon";
import styles from "./IconButton.module.css";

/**
 * IconButton — icon-only button primitive.
 *
 * Matches the Figma IconButton component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1011:932). A specialised Button shape
 * for actions represented entirely by an icon: close, dismiss, copy,
 * settings, etc. The `label` prop is REQUIRED (not optional like on
 * regular Icon) because the button has no static visible text to describe
 * its action — screen readers need the aria-label, and hover gets a
 * tooltip via `title` (see Accessibility below).
 *
 * Variants (from Figma property1 enum):
 *   - `default` — transparent background, no border. Paints
 *                 `--icon-bg-hover` on hover. Used for close/dismiss
 *                 and subtle in-card actions.
 *   - `outline` — transparent background, 1px `--icon-outline` border.
 *                 Paints `--icon-bg-hover` on hover and keeps its
 *                 border. Used when the button needs more visual weight
 *                 (e.g. standalone on a surface).
 *
 * Sizes (from Figma size enum):
 *   - `s` — 16px glyph + 4px padding (~24px box)
 *   - `m` — 20px glyph + 4px padding (~28px box, default)
 *   - `l` — 20px glyph + 8px padding (~36px box)
 *
 * Radius is `--radius-4` (4px) uniformly, which is tighter than
 * Button's `--radius-8` — IconButton is a smaller target shape so the
 * smaller corner reads correctly.
 *
 * `subtle` and `danger` variants from the earlier scaffold are
 * intentionally removed: the Figma file defines only Default and
 * Outline, and no consumers use the dropped variants.
 *
 * Accessibility:
 *   - `label` becomes `aria-label` on the button, and the prop list
 *     omits `aria-label` from the spread so a consumer can't silently
 *     override it.
 *   - `label` also defaults `title`, giving every IconButton a native
 *     hover tooltip for free. Unlike `aria-label`, `title` IS in the
 *     spread type, so a consumer can override it by passing `title`
 *     explicitly.
 *   - `type="button"` default prevents accidental form submission.
 *   - Focus-visible renders a 1px outset `--text-primary` box-shadow
 *     ring, matching Button so sibling interactive primitives share a
 *     consistent keyboard affordance.
 */

type Variant = "default" | "outline";
type Size = "s" | "m" | "l";

export type IconButtonProps = {
  /** The icon component to render inside the button. */
  icon: IconComponent;
  /** Required accessible label — becomes aria-label and the default hover-tooltip (title) on the button. */
  label: string;
  variant?: Variant;
  size?: Size;
  className?: string;
} & Omit<ComponentPropsWithoutRef<"button">, "className" | "aria-label" | "children">;

// Glyph size per IconButton size. s → 16px, m/l → 20px per Figma spec.
const GLYPH_SIZE: Record<Size, "sm" | "md"> = {
  s: "sm",
  m: "md",
  l: "md",
};

export function IconButton({
  icon,
  label,
  variant = "default",
  size = "m",
  type,
  className,
  title,
  ...rest
}: IconButtonProps) {
  const classes = [
    styles.iconButton,
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type={type ?? "button"}
      className={classes}
      aria-label={label}
      title={title ?? label}
      {...rest}
    >
      <Icon as={icon} size={GLYPH_SIZE[size]} tone="inherit" />
    </button>
  );
}
